import { createSlice } from "@reduxjs/toolkit";
// ⚠️ `userLogout` / `supportLogout` exist ONLY in LoginSlice/index.desktop.ts — hazard 6.
import { supportLogout, userLogout } from "../LoginSlice";

export * from "./shared";   // rule 4: the folder always has all three files

/**
 * WEB's checkout slice. `apps/mobile` does NOT have one of its own — its `CheckoutSlice.ts` is a
 * pure re-export of `@dotpe/rista-pos-common`'s `commoncheckoutSlice`, which is what
 * `index.native.ts` next to this file reproduces. So the two platforms share the store key
 * (`checkoutModalStore`) and the slice `name` (`"checkout"`) but not a single line of code, and
 * `shared.ts` therefore holds no code, though it exists per standing rule 4 and records why.
 *
 * ⚠️ **Hazard 1 re-checked before this uplift, and it is clean.** `"checkout"` used to be carried
 * by FOUR slices; `CartSlice` was renamed to `"cart"` in `2c0625f54`, leaving exactly one
 * `"checkout"` producer per platform — web's slice below, and rista-pos-common's on mobile.
 * Verified again: one `name: "checkout"` in `apps/web/src`, zero string-literal `"checkout/..."`
 * action types anywhere, zero `action.type.startsWith/includes` prefix matching, and
 * `checkoutModalStore` is in NEITHER app's redux-persist whitelist.
 *
 * ⚠️ `updatePaymentFailure` (correctly spelled) is declared and exported here but has NO mobile
 * counterpart — rista-pos-common exports only the typo'd `updatePaymnetFailure`, which web has
 * too. Both are preserved; see the ledger.
 */
export interface ContactInfo {
  name: string | null;
  phoneNumber: string | null;
}
export interface RistaCard {
  contactInfo: ContactInfo;
  cardNumber: string;
  expiryDate: string; 
  branchId: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
  deviceIdentifier: string;
  createdBy: string;
  createdDate: string;
  version: number;
  mid: string;
  balance: number;
}
export interface CheckoutState {
  checkoutModalState: boolean,
  paidAmount: number | string,
  tipAmount: number | string,
  checkoutSuccess: any,
  isLoading: boolean,
  isCheckoutActive: boolean,
  paymentSuccess: boolean,
  transactionType: string,
  saleStatus: any,
  isSplitPayment: boolean,
  isResetReference: boolean,
  paymentFlow: string,
  toastInfo: any,
  infoModalState: any,
  refetchData: boolean,
  hideFullPayment: boolean,
  isCheckoutTrigger: boolean,
  modifySelectedPayment: boolean,
  showPaymentModal: boolean,
  paymentStatusFromNative: {};
  checkoutCallback: any;
  returnSale: boolean;
  ristaCardList: RistaCard[];
  postSaleData?: any;
}

const initialState: CheckoutState = {
  checkoutModalState: false,
  paidAmount: "00.00",
  tipAmount: 0,
  checkoutSuccess: {},
  isLoading: false,
  isCheckoutActive: false,
  paymentSuccess: false,
  transactionType: 'fullPayment',
  saleStatus: {},
  isSplitPayment: false,
  isResetReference: false,
  paymentFlow: '',
  toastInfo: {},
  infoModalState: {},
  refetchData: false,
  hideFullPayment: false,
  isCheckoutTrigger: false,
  modifySelectedPayment: false,
  showPaymentModal: false,
  paymentStatusFromNative: {
    success: false
  },
  checkoutCallback: () => {},
  returnSale: false,
  ristaCardList: []
};

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    toggleCheckoutState: (state, action) => {
      state.checkoutModalState = action.payload;
    },
    updateCheckoutCallback: (state, action) => {
      state.checkoutCallback = action.payload;
    },
    updatePaidAmount: (state, action) => {
      state.paidAmount = action.payload;
    },
    updateTipAmount: (state, action) => {
      state.tipAmount = action.payload;
    },
    updatePaymentToServer: (state, action) => {
      state.isLoading = true;
    },
    updatePaymentSuccess: (state, action) => {
      state.isLoading = false;
      state.checkoutSuccess = action.payload;
    },
    updatePaymentClose: (state, action) => {
      state.isLoading = true;
    },
    updatePaymnetFailure: (state) => {
      state.isLoading = false;
    },
    showConfirmCta: (state, action) => {
      state.isCheckoutActive = action.payload;
    },
    paymentSuccessPopup: (state, action) => {
      if (action.payload) {
        state.paymentSuccess = true;
        state.postSaleData = action.payload;
      } else {
        state.paymentSuccess = false;
        state.postSaleData = null;
      }
    },
    updateTransactionType: (state, action) => {
      state.transactionType = action.payload;
    },
    updateSaleStatus: (state, action) => {
      state.saleStatus = action.payload;
    },
    updatePaymentType: (state, action) => {
      state.isSplitPayment = action.payload;
    },
    resetReference: (state, action) => {
      state.isResetReference = action.payload;
    },
    updatePaymentFlow: (state, action) => {
      state.paymentFlow = action.payload;
    },
    handleSaveSaleData: (state, action) => {
      state.isLoading = true;
    },
    updateUpiPayment: (state, action) => {
      state.isLoading = true;
    },
    updatePrepaidPayment: (state, action) => {
      state.isLoading = true;
    },
    updatePaymentFailure: (state, action) => {
      state.isLoading = false;
    },
    updateUpiPaymentSuccess: (state, action) => {
      state.isLoading = false;
    },
    updateToastInfo: (state, action) => {
      state.toastInfo = action.payload;
    },
    updateInfoModal: (state, action) => {
      state.infoModalState = action.payload;
    },
    updateRefetchData: (state) => {
      state.refetchData = true;
    },
    resetRefetch: (state) => {
      state.refetchData = false;
    },
    hideFullPayment: (state, action) => {
      state.hideFullPayment = action.payload;
    },
    updateCheckoutTrigger: (state, action) => {
      state.isCheckoutTrigger = action.payload;
    },
    updateSalePayment: (state, action) => {

    },
    getExtStatus: (state, action) => {
      state.isLoading = true;
    },
    updateCancelPayment: (state, action) => {
      state.isLoading = true;
    },
    refundPayment: (state, action) => {
      state.isLoading = true;
    },
    updateSelectedPayment: (state, action) => {
      state.modifySelectedPayment = action.payload;
    },
    updatePaymentModal: (state, action) => {
      state.showPaymentModal = action.payload;
    },
    updateCheckoutSlice: (state, action) => {
      return action.payload;
    },
    setNativePaymentData: (state, action) => {
      state.paymentStatusFromNative = action.payload;
    },
    isReturnSale: (state, action) => {
      state.returnSale = action.payload
    },
    saleReturnClose: (state, action) => {},
    checkForProviderBalance: (state, action) => {},
    checkForOtp: (state, action) => {},
    getExtLinkForBalance: (state, action) => {},
    fetchRistaCards: (state, action) => {
      state.isLoading = true;
    },
    setRistaCardList: (state, action) => {
      state.isLoading = false;
      state.ristaCardList = action.payload;
    },
    validateRistaCard: (state, action) => {
      state.isLoading = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {
        
      })
      .addCase(supportLogout, (state) => {
        
      });
  },
});

export const {
  toggleCheckoutState,
  updatePaidAmount,
  updateTipAmount,
  updatePaymentToServer,
  updatePaymentSuccess,
  updatePaymnetFailure,
  showConfirmCta,
  paymentSuccessPopup,
  updateTransactionType,
  updateSaleStatus,
  updatePaymentType,
  resetReference,
  updatePaymentFlow,
  handleSaveSaleData,
  updatePrepaidPayment,
  updateUpiPayment,
  updateUpiPaymentSuccess,
  updateToastInfo,
  updateInfoModal,
  updateRefetchData,
  resetRefetch,
  updatePaymentClose,
  updateCheckoutTrigger,
  hideFullPayment,
  updateSalePayment,
  getExtStatus,
  refundPayment,
  updateCancelPayment,
  updateSelectedPayment,
  updatePaymentModal,
  updateCheckoutSlice,
  setNativePaymentData,
  updateCheckoutCallback,
  isReturnSale,
  saleReturnClose,
  checkForProviderBalance,
  checkForOtp,
  getExtLinkForBalance,
  fetchRistaCards,
  setRistaCardList,
  validateRistaCard,
  updatePaymentFailure
} = checkoutSlice.actions;
export default checkoutSlice.reducer;